import { ApiClient } from './ApiClient'
import type { RequestOptions } from './types'

export abstract class ServiceBase {
  protected client: ApiClient
  protected basePath: string

  constructor(client: ApiClient, basePath: string) {
    this.client = client
    this.basePath = basePath
  }

  protected buildUrl(endpoint: string): string {
    // 确保basePath和endpoint都以正确的斜杠格式连接
    const cleanBasePath = this.basePath.replace(/\/$/, '')
    const cleanEndpoint = endpoint.replace(/^\//, '')
    return `${cleanBasePath}/${cleanEndpoint}`
  }

  protected async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.client.get<T>(this.buildUrl(endpoint), options)
  }

  protected async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.client.post<T>(this.buildUrl(endpoint), data, options)
  }

  protected async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.client.put<T>(this.buildUrl(endpoint), data, options)
  }

  protected async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.client.delete<T>(this.buildUrl(endpoint), options)
  }

  protected async upload<T>(endpoint: string, file: File, options?: RequestOptions): Promise<T> {
    return this.client.upload<T>(this.buildUrl(endpoint), file, options)
  }

  protected async download(endpoint: string, options?: RequestOptions): Promise<Blob> {
    return this.client.download(this.buildUrl(endpoint), options)
  }

  // 便捷方法：处理分页请求
  protected async getPaginated<T>(
    endpoint: string, 
    page?: number, 
    limit?: number, 
    options?: RequestOptions
  ): Promise<T> {
    const params = {
      ...(options?.params || {}),
      ...(page !== undefined && { skip: (page - 1) * (limit || 20) }),
      ...(limit !== undefined && { limit }),
    }

    return this.get<T>(endpoint, { ...options, params })
  }

  // 便捷方法：处理资源CRUD操作
  protected async getById<T>(id: number | string): Promise<T> {
    return this.get<T>(`/${id}`)
  }

  protected async create<T>(data: unknown): Promise<T> {
    return this.post<T>('', data)
  }

  protected async updateById<T>(id: number | string, data: unknown): Promise<T> {
    return this.put<T>(`/${id}`, data)
  }

  protected async deleteById<T>(id: number | string): Promise<T> {
    return this.delete<T>(`/${id}`)
  }

  // Mock模式支持
  protected isMockEnabled(): boolean {
    return import.meta.env.VITE_ENABLE_MOCK === 'true'
  }

  protected async withMockFallback<T>(
    mockFn: () => Promise<T>,  
    realFn: () => Promise<T>
  ): Promise<T> {
    return this.isMockEnabled() ? mockFn() : realFn()
  }
}